/**
 * Feedback Management Page
 * View and manage user feedback and feature requests
 */
// @ts-nocheck

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Flex,
  Heading,
  HStack,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  Badge,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  useToast,
  Spinner,
  VStack,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  SimpleGrid,
  useColorModeValue,
  Avatar,
  Tooltip,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  useDisclosure,
  Textarea,
  FormControl,
  FormLabel,
} from '@chakra-ui/react';
import {
  MdSearch,
  MdRefresh,
  MdMoreVert,
  MdVisibility,
  MdThumbUp,
  MdThumbDown,
  MdStar,
  MdBugReport,
  MdLightbulb,
  MdFeedback,
  MdCheckCircle,
  MdCancel,
  MdReply,
} from 'react-icons/md';
import { Feedback, FeedbackStats, FeedbackFilters, SupportTicket } from '../../types/support';
import { supportService } from '../services/supportService';

export function FeedbackPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const { isOpen: isResponseOpen, onOpen: onResponseOpen, onClose: onResponseClose } = useDisclosure();
  
  const [feedbackList, setFeedbackList] = useState<Feedback[]>([]);
  const [stats, setStats] = useState<FeedbackStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedFeedback, setSelectedFeedback] = useState<Feedback | null>(null);
  const [response, setResponse] = useState('');
  const [filters, setFilters] = useState<FeedbackFilters>({
    search: '',
    type: 'all',
    status: 'all',
    priority: 'all',
  });

  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');

  useEffect(() => {
    loadFeedback();
  }, [filters]);

  const loadFeedback = async () => {
    try {
      setLoading(true);
      const response = await supportService.getFeedback(filters);
      setFeedbackList(response.feedback);
      // Stats would need a separate API call if needed
    } catch (error: any) {
      console.error('Error loading feedback:', error);
      toast({
        title: 'Error loading feedback',
        description: error.message || 'Please try again later',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      // Fallback to mock data on error
      setFeedbackList(mockFeedback);
      setStats(mockStats);
    } finally {
      setLoading(false);
    }
  };

  const openResponseModal = (feedback: Feedback) => {
    setSelectedFeedback(feedback);
    setResponse(feedback.publicResponse || '');
    onResponseOpen();
  };

  const submitResponse = async () => {
    if (!selectedFeedback) return;

    try {
      // TODO: Implement feedback response update in supportService
      // await supportService.updateFeedback(selectedFeedback.id, {
      //   publicResponse: response,
      //   status: 'reviewed',
      // });
      toast({
        title: 'Feature not yet implemented',
        description: 'Feedback response update coming soon',
        status: 'info',
        duration: 3000,
      });
      onResponseClose();
      loadFeedback();
    } catch (error: any) {
      toast({
        title: 'Error sending response',
        description: error.message || 'Please try again later',
        status: 'error',
        duration: 5000,
      });
    }
  };

  const getTypeColor = (type: Feedback['type']) => {
    switch (type) {
      case 'bug': return 'red';
      case 'feature_request': return 'blue';
      case 'improvement': return 'purple';
      case 'praise': return 'green';
      case 'complaint': return 'orange';
      case 'other': return 'gray';
      default: return 'gray';
    }
  };

  const getTypeIcon = (type: Feedback['type']) => {
    switch (type) {
      case 'bug': return MdBugReport;
      case 'feature_request': return MdLightbulb;
      case 'praise': return MdStar;
      default: return MdFeedback;
    }
  };

  const getStatusColor = (status: Feedback['status']) => {
    switch (status) {
      case 'new': return 'blue';
      case 'reviewed': return 'purple';
      case 'planned': return 'cyan';
      case 'in_progress': return 'orange';
      case 'completed': return 'green';
      case 'wont_fix': return 'gray';
      case 'duplicate': return 'gray';
      default: return 'gray';
    }
  };

  const getSentimentColor = (sentiment?: Feedback['sentiment']) => {
    switch (sentiment) {
      case 'positive': return 'green';
      case 'neutral': return 'gray';
      case 'negative': return 'red';
      default: return 'gray';
    }
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('en-CA', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  return (
    <Box>
      {/* Page Header */}
      <Flex justify="space-between" align="center" mb={6}>
        <VStack align="start" spacing={0}>
          <Heading size="lg">Feedback Management</Heading>
          <Text color="gray.600" fontSize="sm">
            Review and respond to user feedback
          </Text>
        </VStack>
        <HStack>
          <Button
            leftIcon={<MdRefresh />}
            onClick={loadFeedback}
            variant="outline"
            isLoading={loading}
          >
            Refresh
          </Button>
        </HStack>
      </Flex>

      {/* Stats Cards */}
      {stats && (
        <SimpleGrid columns={{ base: 1, md: 2, lg: 5 }} spacing={4} mb={6}>
          <Card bg={cardBg} borderColor={borderColor}>
            <CardBody>
              <Stat>
                <StatLabel>Total Feedback</StatLabel>
                <StatNumber>{stats.total}</StatNumber>
                <StatHelpText>{stats.new} new</StatHelpText>
              </Stat>
            </CardBody>
          </Card>

          <Card bg={cardBg} borderColor={borderColor}>
            <CardBody>
              <Stat>
                <StatLabel>Feature Requests</StatLabel>
                <StatNumber>{stats.byType.feature_request || 0}</StatNumber>
                <StatHelpText>{stats.byStatus.planned || 0} planned</StatHelpText>
              </Stat>
            </CardBody>
          </Card>

          <Card bg={cardBg} borderColor={borderColor}>
            <CardBody>
              <Stat>
                <StatLabel>Bug Reports</StatLabel>
                <StatNumber>{stats.byType.bug || 0}</StatNumber>
                <StatHelpText>{stats.byStatus.in_progress || 0} in progress</StatHelpText>
              </Stat>
            </CardBody>
          </Card>

          <Card bg={cardBg} borderColor={borderColor}>
            <CardBody>
              <Stat>
                <StatLabel>Avg Rating</StatLabel>
                <StatNumber fontSize="lg">{stats.avgRating.toFixed(1)} / 5</StatNumber>
                <StatHelpText color={stats.avgRating >= 4 ? 'green.500' : 'orange.500'}>
                  From {stats.totalWithRating} ratings
                </StatHelpText>
              </Stat>
            </CardBody>
          </Card>

          <Card bg={cardBg} borderColor={borderColor}>
            <CardBody>
              <Stat>
                <StatLabel>Sentiment</StatLabel>
                <HStack spacing={2}>
                  <Tooltip label="Positive">
                    <HStack>
                      <Icon as={MdThumbUp} color="green.500" />
                      <Text fontSize="sm">{stats.bySentiment.positive || 0}</Text>
                    </HStack>
                  </Tooltip>
                  <Tooltip label="Negative">
                    <HStack>
                      <Icon as={MdThumbDown} color="red.500" />
                      <Text fontSize="sm">{stats.bySentiment.negative || 0}</Text>
                    </HStack>
                  </Tooltip>
                </HStack>
              </Stat>
            </CardBody>
          </Card>
        </SimpleGrid>
      )}

      {/* Filters */}
      <Card bg={cardBg} borderColor={borderColor} mb={6}>
        <CardBody>
          <SimpleGrid columns={{ base: 1, md: 4 }} spacing={4}>
            <InputGroup>
              <InputLeftElement>
                <Icon as={MdSearch} color="gray.400" />
              </InputLeftElement>
              <Input
                placeholder="Search feedback..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              />
            </InputGroup>

            <Select
              value={filters.type}
              onChange={(e) => setFilters({ ...filters, type: e.target.value as any })}
            >
              <option value="all">All Types</option>
              <option value="bug">Bug Reports</option>
              <option value="feature_request">Feature Requests</option>
              <option value="improvement">Improvements</option>
              <option value="praise">Praise</option>
              <option value="complaint">Complaints</option>
              <option value="other">Other</option>
            </Select>

            <Select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value as any })}
            >
              <option value="all">All Status</option>
              <option value="new">New</option>
              <option value="reviewed">Reviewed</option>
              <option value="planned">Planned</option>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
              <option value="wont_fix">Won't Fix</option>
              <option value="duplicate">Duplicate</option>
            </Select>

            <Select
              value={filters.priority}
              onChange={(e) => setFilters({ ...filters, priority: e.target.value as any })}
            >
              <option value="all">All Priorities</option>
              <option value="critical">Critical</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </Select>
          </SimpleGrid>
        </CardBody>
      </Card>

      {/* Feedback Table */}
      <Card bg={cardBg} borderColor={borderColor}>
        <CardHeader>
          <Flex justify="space-between" align="center">
            <Heading size="md">Feedback</Heading>
            <Text fontSize="sm" color="gray.600">
              {feedbackList.length} items
            </Text>
          </Flex>
        </CardHeader>
        <CardBody>
          {loading ? (
            <Flex justify="center" align="center" h="200px">
              <Spinner size="xl" color="blue.500" />
            </Flex>
          ) : feedbackList.length === 0 ? (
            <Flex justify="center" align="center" h="200px" direction="column">
              <Icon as={MdFeedback} boxSize={12} color="gray.400" mb={2} />
              <Text color="gray.600">No feedback found</Text>
            </Flex>
          ) : (
            <Box overflowX="auto">
              <Table variant="simple">
                <Thead>
                  <Tr>
                    <Th>User</Th>
                    <Th>Type</Th>
                    <Th>Title</Th>
                    <Th>Priority</Th>
                    <Th>Rating</Th>
                    <Th>Sentiment</Th>
                    <Th>Status</Th>
                    <Th>Votes</Th>
                    <Th>Created</Th>
                    <Th>Actions</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {feedbackList.map((feedback) => (
                    <Tr
                      key={feedback.id}
                      _hover={{ bg: hoverBg }}
                      cursor="pointer"
                      onClick={() => navigate(`/admin/support/feedback/${feedback.id}`)}
                    >
                      <Td>
                        <HStack>
                          <Avatar size="sm" name={feedback.userName} />
                          <VStack align="start" spacing={0}>
                            <Text fontSize="sm" fontWeight="medium">{feedback.userName}</Text>
                            <Badge fontSize="xs" colorScheme="purple">{feedback.userType}</Badge>
                          </VStack>
                        </HStack>
                      </Td>
                      <Td>
                        <HStack>
                          <Icon as={getTypeIcon(feedback.type)} color={`${getTypeColor(feedback.type)}.500`} />
                          <Badge colorScheme={getTypeColor(feedback.type)} fontSize="xs">
                            {feedback.type.replace('_', ' ')}
                          </Badge>
                        </HStack>
                      </Td>
                      <Td>
                        <VStack align="start" spacing={0}>
                          <Text fontWeight="bold" noOfLines={1}>{feedback.title}</Text>
                          <Text fontSize="sm" color="gray.600" noOfLines={1}>
                            {feedback.description}
                          </Text>
                        </VStack>
                      </Td>
                      <Td>
                        <Badge 
                          colorScheme={
                            feedback.priority === 'critical' ? 'red' :
                            feedback.priority === 'high' ? 'orange' :
                            feedback.priority === 'medium' ? 'yellow' : 'gray'
                          }
                        >
                          {feedback.priority}
                        </Badge>
                      </Td>
                      <Td>
                        {feedback.rating ? (
                          <HStack>
                            <Icon as={MdStar} color="yellow.400" />
                            <Text fontWeight="bold">{feedback.rating}</Text>
                          </HStack>
                        ) : (
                          <Text fontSize="sm" color="gray.500">N/A</Text>
                        )}
                      </Td>
                      <Td>
                        {feedback.sentiment && (
                          <Badge colorScheme={getSentimentColor(feedback.sentiment)}>
                            {feedback.sentiment}
                          </Badge>
                        )}
                      </Td>
                      <Td>
                        <Badge colorScheme={getStatusColor(feedback.status)}>
                          {feedback.status.replace('_', ' ')}
                        </Badge>
                      </Td>
                      <Td>
                        <HStack>
                          <Icon as={MdThumbUp} color="gray.400" />
                          <Text fontWeight="bold">{feedback.votes}</Text>
                        </HStack>
                      </Td>
                      <Td>
                        <Text fontSize="sm">{formatDate(feedback.createdAt)}</Text>
                      </Td>
                      <Td onClick={(e) => e.stopPropagation()}>
                        <Menu>
                          <MenuButton
                            as={IconButton}
                            icon={<MdMoreVert />}
                            variant="ghost"
                            size="sm"
                          />
                          <MenuList>
                            <MenuItem
                              icon={<MdVisibility />}
                              onClick={() => navigate(`/admin/support/feedback/${feedback.id}`)}
                            >
                              View Details
                            </MenuItem>
                            <MenuItem
                              icon={<MdReply />}
                              onClick={() => openResponseModal(feedback)}
                            >
                              Respond
                            </MenuItem>
                            <MenuItem icon={<MdCheckCircle />}>
                              Mark as Completed
                            </MenuItem>
                            <MenuItem icon={<MdCancel />}>
                              Mark as Won't Fix
                            </MenuItem>
                          </MenuList>
                        </Menu>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          )}
        </CardBody>
      </Card>

      {/* Response Modal */}
      <Modal isOpen={isResponseOpen} onClose={onResponseClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Respond to Feedback</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {selectedFeedback && (
              <VStack spacing={4} align="stretch">
                <Box>
                  <Text fontWeight="bold" mb={2}>{selectedFeedback.title}</Text>
                  <Text fontSize="sm" color="gray.600">{selectedFeedback.description}</Text>
                </Box>
                <FormControl>
                  <FormLabel>Public Response</FormLabel>
                  <Textarea
                    value={response}
                    onChange={(e) => setResponse(e.target.value)}
                    placeholder="Write your response to the user..."
                    rows={6}
                  />
                </FormControl>
              </VStack>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onResponseClose}>
              Cancel
            </Button>
            <Button colorScheme="blue" onClick={submitResponse}>
              Send Response
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}

// Mock Data
const mockFeedback: Feedback[] = [
  {
    id: '1',
    userId: 'U001',
    userName: 'John Smith',
    userEmail: 'john@example.com',
    userType: 'landlord',
    type: 'feature_request',
    title: 'Add bulk payment import feature',
    description: 'Would love to be able to import multiple payments at once via CSV',
    category: 'payments',
    rating: 5,
    sentiment: 'positive',
    page: '/payments',
    feature: 'Payment Import',
    status: 'planned',
    priority: 'high',
    votes: 12,
    createdAt: new Date('2025-11-20'),
    updatedAt: new Date('2025-11-22'),
    tags: ['payments', 'import', 'bulk'],
    assignedTeam: 'engineering',
  },
  {
    id: '2',
    userId: 'U002',
    userName: 'Sarah Johnson',
    userEmail: 'sarah@example.com',
    userType: 'landlord',
    type: 'bug',
    title: 'Gmail sync occasionally fails',
    description: 'Sometimes the Gmail integration stops syncing and I have to reconnect',
    category: 'integrations',
    rating: 3,
    sentiment: 'negative',
    status: 'in_progress',
    priority: 'high',
    votes: 8,
    createdAt: new Date('2025-11-18'),
    updatedAt: new Date('2025-11-23'),
    tags: ['gmail', 'sync', 'bug'],
    assignedTeam: 'engineering',
  },
];

const mockStats: FeedbackStats = {
  total: 2,
  new: 0,
  byType: {
    bug: 1,
    feature_request: 1,
    improvement: 0,
    praise: 0,
    complaint: 0,
    other: 0,
  },
  byStatus: {
    new: 0,
    reviewed: 0,
    planned: 1,
    in_progress: 1,
    completed: 0,
    wont_fix: 0,
    duplicate: 0,
  },
  bySentiment: {
    positive: 1,
    neutral: 0,
    negative: 1,
  },
  avgRating: 4.0,
  totalWithRating: 2,
};
